import { Typography, Divider, Box, Button, Tooltip } from '@mui/material';
import { useAppSelector } from 'app/hooks';
import VariablesImage from 'assets/images/variablesExample.png';
import VariableDisplay from './components/VariableDisplay';
import styles from './VariablesSidebar.module.css';
import EditIcon from '@mui/icons-material/Edit';
import { selectContract, selectQuote, selectLoan } from 'features/salesforce/salesforceSlice';
import { selectDocumentType, DocumentType } from 'features/document/documentSlice';
import { openInNewTab } from 'utils/openInNewTab';
import { selectIsSalesforceUrl } from 'features/auth/authSlice';
import { InfoRounded } from '@mui/icons-material';
import { capitalizeFirstLetter } from 'utils/stringUtils';

export default function VariablesSidebar() {
  const contract = useAppSelector(selectContract) || {};
  const quote = useAppSelector(selectQuote) || {};
  const loan = useAppSelector(selectLoan) || {};
  const documentType = useAppSelector(selectDocumentType);
  const salesforceBaseUrl = useAppSelector(selectIsSalesforceUrl);
  const variablesSource = {
    [DocumentType.CONTRACT]: contract,
    [DocumentType.QUOTE]: quote,
    [DocumentType.PRECLOSING]: loan,
  };
  const variablesArray = [];
  const variablesData = variablesSource[documentType as keyof typeof variablesSource];
  if (variablesData) {
    for (const variable in variablesData) {
      if (Object.prototype.hasOwnProperty.call(variablesData, variable)) {
        variablesArray.push({
          variableName: variable,
          variableValue: variablesData[variable as keyof typeof variablesData],
        });
      }
    }
  }

  const handleClickEditContract = () => {
    if (salesforceBaseUrl && variablesData.Id) {
      openInNewTab(`${salesforceBaseUrl}/lightning/r/${documentType}/${variablesData.Id}/edit? `);
    }
  };

  return (
    <Box color='#474747'>
      <Typography fontSize={'16px'} fontWeight={500} marginBottom={'24px'}>
        Variables
      </Typography>
      <Typography fontSize={'16px'} marginBottom={'24px'}>
        A variable is like a label that holds a specific word or value. Instead of typing the same word over and over
        again, you can use a variable.
      </Typography>
      <Typography fontSize={'16px'} marginBottom={'24px'}>
        Here&apos;s how to use it:
      </Typography>

      <ol className={styles.stepsList}>
        <li>Look at the variables library below.</li>
        <li>
          <span>Copy the variable</span> by clicking on the <span>copy icon button</span>
        </li>
        <li>
          In your file, <span>paste the variable</span> in a text block (<span>Ctrl+V</span> for Windows and{' '}
          <span>Command+V</span> for Mac).
        </li>
        <li>
          To delete a variable, position your cursor next to the right side and <span>press the delete key.</span>
        </li>
      </ol>

      <Typography fontSize={'16px'} marginBottom={'10px'}>
        Here&apos;s how to identify it inside a text block:
        <Tooltip
          title='The first example is a variable containing a value already assigned. The second example is a variable without a value.'
          arrow
          placement='top'
          className={styles.infoTip}
        >
          <InfoRounded color='primary' fontSize='small' />
        </Tooltip>
      </Typography>

      <img src={VariablesImage} alt='Variables example' />

      <Divider className={styles.divider} />

      <Typography fontSize={'16px'} marginBottom={'10px'}>
        Library
      </Typography>
      <Typography fontSize={'16px'} marginBottom={'15px'}>
        {`Showing ${variablesArray.length} variables`}
      </Typography>

      {!!variablesData?.Id && (
        <Button
          startIcon={<EditIcon />}
          sx={{ textTransform: 'capitalize', marginBottom: '10px' }}
          variant='outlined'
          fullWidth
          onClick={() => {
            handleClickEditContract();
          }}
        >
          Edit {`${capitalizeFirstLetter(documentType)}`} In CRM
        </Button>
      )}

      {variablesArray.map((variable, index) => (
        <VariableDisplay
          key={`${contract.Id}-${index}`}
          variableName={variable.variableName}
          variableValue={variable.variableValue}
        />
      ))}
    </Box>
  );
}
